import * as React from "react";

import type { ScanType } from "../api";

import { SubmitHandler, useForm, Controller } from "react-hook-form";

import { Button, SystemMessage } from "@scandotcom/react";
import { useBodyParts } from "../NewSearchResults/SearchForm/useBodyParts";
import { BodyPartInput } from "../Components/Search/BodyPartsInput";
import { ScanTypeInput } from "../Components/Search/ScanTypeInput";
import { LocationInput } from "../Components/Search/LocationInput";

interface Props {
  scanTypes: ScanType[];
  selectedScanType: Pick<ScanType, "id" | "name" | "max_allowed_body_parts">;
}

export interface SearchFormFields {
  location: string;
  bodyPartIds: number[];
  scanTypeId: string;
}

const csrfToken = document
  .querySelector("meta[name='csrf-token']")
  ?.getAttribute("content");

/**
 * A variant of the Search component that is tailored for searches
 * from service pages. A service may or may not have the scan-type
 * pre-set to a single option
 *
 * @param props
 * @returns
 */
export const ServiceSearchForm: React.FC<Props> = (props) => {
  const [loading, setLoading] = React.useState(false);

  const formRef = React.useRef<HTMLFormElement | null>(null);

  const form = useForm<SearchFormFields>({
    defaultValues: {
      location: "",
      bodyPartIds: [],
      scanTypeId: props.selectedScanType.id,
    },
  });

  const onSubmit: SubmitHandler<SearchFormFields> = () => {
    setLoading(true);
    if (formRef.current) {
      formRef.current.submit();
      return;
    }
    setLoading(false);
  };

  const selectedScanTypeId = form.watch("scanTypeId");

  const { bodyParts } = useBodyParts({
    initialBodyParts: [],
    selectedScanTypeId: selectedScanTypeId,
    onFetchSuccess: (bodyParts) => {
      // when the available body parts changes, update the selections in the form to
      // include only the body parts that are valid for the current scan type. Also
      // adjust the max selected body parts for the scan type
      const currentBodyPartIds = form.getValues("bodyPartIds");
      const availableBodyPartIds = bodyParts.map(({ id }) => id);

      form.setValue(
        "bodyPartIds",
        currentBodyPartIds
          .filter((id) => availableBodyPartIds.includes(id))
          .slice(0, props.selectedScanType?.max_allowed_body_parts || 4)
      );
    },
  });

  const selectedScanType = props.scanTypes.find(
    ({ id }) => selectedScanTypeId === id
  );

  return (
    <form
      action="/searches"
      acceptCharset="UTF-8"
      method="post"
      onSubmit={form.handleSubmit(onSubmit)}
      ref={formRef}
      data-testid="form"
      className="border-cream-400 rounded-2xl border p-4"
    >
      {csrfToken ? (
        <input name="authenticity_token" type="hidden" value={csrfToken} />
      ) : null}
      <input name="scan_type" value={props.selectedScanType.id} type="hidden" />

      <div className={""}>
        <div className="">
          <Controller
            name="scanTypeId"
            control={form.control}
            rules={{ required: true }}
            render={({ field: { name, onChange, value }, fieldState }) => (
              <ScanTypeInput
                name={name}
                availableScanTypes={props.scanTypes}
                errorId={
                  fieldState.error?.message ? "scan_type-error" : undefined
                }
                selectedScanTypeId={value ?? ""}
                displayValue={selectedScanType?.name}
                setScanType={onChange}
                className="border-cream-300 border-b pb-4"
                inputClassName="min-h-11"
              />
            )}
          />

          {form.watch("bodyPartIds").map((item) => (
            <React.Fragment key={item}>
              <input name="body_parts[]" value={item} type="hidden" />
              <input name="search_inputs[]" value={item} type="hidden" />
            </React.Fragment>
          ))}

          <Controller
            name="bodyPartIds"
            control={form.control}
            rules={{ required: "Select a body part" }}
            render={({ field: { onChange, value, name }, fieldState }) => (
              <div>
                <BodyPartInput
                  isGrouped
                  name={name}
                  availableBodyParts={bodyParts}
                  setSelectedBodyPartIds={onChange}
                  selectedBodyPartIds={value}
                  maxBodyParts={
                    props.selectedScanType?.max_allowed_body_parts || 4
                  }
                  className="border-cream-300 border-b py-4"
                  inputClassName="min-h-11"
                  errorId={fieldState.error ? "body_parts-error" : undefined}
                />
                {fieldState.error && (
                  <SystemMessage
                    type="error"
                    id="body_parts-error"
                    className="my-4"
                  >
                    {fieldState.error.message}
                  </SystemMessage>
                )}
              </div>
            )}
          />
          <div className="">
            <Controller
              name="location"
              control={form.control}
              rules={{ required: "Enter a location" }}
              render={({ field, fieldState }) => {
                return (
                  <div>
                    <LocationInput
                      onChange={field.onChange}
                      name="area"
                      inputClassName="min-h-11"
                      className="py-4"
                      errorId={
                        fieldState.error ? "body_parts-error" : undefined
                      }
                    />
                    {fieldState.error && (
                      <SystemMessage
                        type="error"
                        id="body_parts-error"
                        className="mb-4"
                      >
                        {fieldState.error.message}
                      </SystemMessage>
                    )}
                  </div>
                );
              }}
            />
          </div>
          <Button
            kind="secondary"
            type="submit"
            className="!bg-cypress-800 hover:!bg-cypress-800/90 block min-h-[53px] w-full text-base"
            disabled={loading}
          >
            Search
          </Button>
        </div>
      </div>
    </form>
  );
};
